import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Sparkles, Rocket, Trash2, Pencil, Eye, EyeOff, X, Loader2, Users, Info, Upload, RefreshCw, Film, Play, Share2, Download, Mail, Package, Link2, Check, Newspaper, ExternalLink, Maximize2 } from "lucide-react";
import { api, apiErr, adminDelete } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (id, rev) => `${BACKEND}/api/publications/${id}/image${rev ? `?v=${rev}` : ""}`;
const videoUrl = (id) => `${BACKEND}/api/publications/${id}/video`;

// Date effective d'une parution : date de publication si publiée, sinon date de création.
const effDate = (p) => new Date((p.status === "published" ? p.published_at : p.created_at) || p.created_at || 0).getTime();
// Tri décroissant : la parution la plus récente en haut.
const sortByDate = (arr) => [...arr].sort((a, b) => effDate(b) - effDate(a));

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const fmtDate = (s) => s ? new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// URL de lecture de l'article : page publique si publié, aperçu admin sinon.
const liveUrl = (p) => `/blog/${p.id}${p.status === "published" ? "" : "?preview=1"}`;

export default function AdminPublications() {
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [shares, setShares] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editTab, setEditTab] = useState("modifier"); // onglet d'ouverture du studio : "apercu" ou "modifier"
  const [regenId, setRegenId] = useState(null);
  const [porting, setPorting] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genLabel, setGenLabel] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [epTopic, setEpTopic] = useState("");
  const progressTimer = useRef(null);

  // Barre de progression simulée (aucun flux serveur temps réel disponible) : monte jusqu'à 92%.
  const startProgress = (label, expectedMs) => {
    setGenLabel(label);
    setGenProgress(5);
    const start = Date.now();
    clearInterval(progressTimer.current);
    progressTimer.current = setInterval(() => {
      const pct = Math.min(92, (Date.now() - start) / expectedMs * 100);
      setGenProgress(pct);
    }, 400);
  };
  const endProgress = () => {
    clearInterval(progressTimer.current);
    setGenProgress(100);
    setTimeout(() => { setGenProgress(0); setGenLabel(""); }, 900);
  };

  // Suit la VRAIE génération d'une vidéo (tâche de fond côté serveur) jusqu'à son terme,
  // puis affiche le résultat réel (succès ou cause d'échec) au lieu d'une barre figée.
  const pollVideoUntilDone = async (id) => {
    for (let i = 0; i < 90; i++) { // jusqu'à ~7,5 min
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const { data } = await api.get("/admin/publications");
        const v = data.find((p) => p.id === id);
        if (v && !v.generating) {
          load();
          if (v.has_video) toast.success("Vidéo générée (En préparation).");
          else toast.error(v.gen_error_detail || "La génération de la vidéo a échoué. Réessayez.");
          return;
        }
      } catch { /* on retente */ }
    }
    load();
    toast.error("La vidéo est toujours en cours. Revenez dans quelques minutes.");
  };

  const copyShare = async (p) => {
    const link = `${BACKEND}/api/share/${p.id}`;
    try { await navigator.clipboard.writeText(link); }
    catch { const t = document.createElement("textarea"); t.value = link; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove(); }
    setCopiedId(p.id);
    toast.success("Lien de partage copié — aperçu image/vidéo inclus.");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const [cvOpen, setCvOpen] = useState(false);
  const [cvTopic, setCvTopic] = useState("");
  const [cvText, setCvText] = useState("");
  const submitCustomVideo = async () => {
    if (!cvTopic.trim()) { toast.error("Indiquez le sujet de la vidéo."); return; }
    if (cvText.length > 220) { toast.error("Texte trop long pour 12 s (max ~220 caractères)."); return; }
    setGenLoading(true);
    startProgress("Génération de la vidéo (sujet + texte) par l'IA…", 9000);
    try {
      const { data } = await api.post("/admin/publications/generate-video-custom", { topic: cvTopic.trim(), text: cvText.trim() });
      toast.success("Vidéo lancée avec votre sujet et votre texte (2 à 5 min) — en préparation.");
      setCvOpen(false); setCvTopic(""); setCvText(""); load();
      await pollVideoUntilDone(data.id);
    } catch (err) { toast.error(apiErr(err)); }
    finally { endProgress(); setGenLoading(false); }
  };

  const exportBlog = async () => {
    setPorting(true);
    try {
      const res = await api.get("/admin/blog/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "aca-blog-export.zip"; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Export du blog téléchargé. Importez-le sur le site en ligne.");
    } catch (err) { toast.error(apiErr(err) || "Export impossible."); }
    finally { setPorting(false); }
  };

  const importBlog = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!window.confirm("Importer ce fichier ? Les articles/vidéos du blog seront remplacés par ceux du fichier.")) return;
    setPorting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/blog/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import réussi : ${data.publications} publication(s), ${data.videos} vidéo(s).`);
      load();
    } catch (err) { toast.error(apiErr(err) || "Import impossible."); }
    finally { setPorting(false); }
  };

  const load = () => {
    api.get("/admin/publications").then((r) => setPosts(sortByDate(r.data))).catch(() => {});
    api.get("/admin/blog/subscribers").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/blog/shares").then((r) => setShares(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Rafraîchissement automatique tant qu'une vidéo est en cours de génération.
  useEffect(() => {
    if (!posts.some((p) => p.generating)) return;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [posts]);

  const generate = async (n = 1) => {
    setGenLoading(true);
    const topic = epTopic.trim();
    startProgress(topic ? `Rédaction de l'épisode « ${topic} » (texte + image)…` : "Rédaction de l'épisode par l'IA (texte + image)…", 38000);
    try {
      await api.post("/admin/publications/generate", { topic: topic || undefined });
      toast.success(topic ? `Épisode « ${topic} » généré (En préparation).` : "Épisode généré (En préparation).");
      setEpTopic("");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { endProgress(); setGenLoading(false); }
  };

  const generateVideo = async () => {
    setGenLoading(true);
    startProgress("Génération de la vidéo par l'IA (12 s)…", 9000);
    try {
      const { data } = await api.post("/admin/publications/generate-video", {});
      toast.success("Vidéo lancée (2 à 5 min) — en préparation.");
      load();
      await pollVideoUntilDone(data.id);
    } catch (err) { toast.error(apiErr(err)); }
    finally { endProgress(); setGenLoading(false); }
  };

  const regenerateVideo = async (p) => {
    if (!window.confirm("Régénérer cette vidéo (12 s, avec logo) ? La vidéo actuelle sera remplacée.")) return;
    setRegenId(p.id);
    try {
      await api.post(`/admin/publications/${p.id}/regenerate-video`);
      toast.success("Nouvelle vidéo en génération (2 à 5 min).");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setRegenId(null); }
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/publications/${p.id}`, { status: p.status === "published" ? "draft" : "published" });
      toast.success(p.status === "published" ? "Publication dépubliée." : "Publication publiée.");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const del = async (p) => {
    if (!window.confirm("Supprimer définitivement cette publication ?")) return;
    try { if (!await adminDelete(`/admin/publications/${p.id}`)) return; toast.success("Publication supprimée."); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const regenerate = async (p) => {
    setRegenId(p.id);
    try {
      await api.post(`/admin/publications/${p.id}/regenerate`);
      toast.success("Article régénéré (texte + image) au nouveau format.");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setRegenId(null); }
  };

  const sendNewsletter = async (p) => {
    if (p.status !== "published") { toast.error("Publiez l'épisode avant d'envoyer la newsletter."); return; }
    if (!window.confirm("Envoyer cet épisode par e-mail à tous les abonnés du blog ?")) return;
    setRegenId(p.id);
    try {
      const { data } = await api.post(`/admin/publications/${p.id}/newsletter`);
      toast.success(`Newsletter envoyée à ${data.sent} abonné(s).`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setRegenId(null); }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="admin-publications">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Blog · Géré par IA</div>
          <h1 className="mt-2 font-display text-3xl font-900 text-white">Publications</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-secondary">
            Générez vos épisodes et vos vidéos <span className="text-white">manuellement, un par un</span>.
            Chaque création reste <span className="text-cyan">en préparation</span> (brouillon) : elle n'apparaît sur le blog qu'après un clic sur « Publier ».
          </p>
        </div>
      </div>

      {/* Section « creer » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-creer"><Sparkles size={13} /> Créer &amp; générer</div>
      <div data-testid="pub-panel-creer">
        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={() => { setEditTab("modifier"); setEdit({}); }} disabled={genLoading} data-testid="pub-create-manual"
            className="aca-btn-secondary">
            <Pencil size={14} /> Créer un article
          </button>
          <button onClick={() => generate(1)} disabled={genLoading} data-testid="pub-generate-one"
            className="aca-btn-primary">
            {genLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Générer un épisode
          </button>
          <button onClick={generateVideo} disabled={genLoading} data-testid="pub-generate-video"
            className="aca-btn-secondary">
            {genLoading ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />} Générer une vidéo (IA)
          </button>
          <button onClick={() => setCvOpen(true)} disabled={genLoading} data-testid="pub-generate-video-custom"
            className="aca-btn-outline">
            <Film size={14} /> Vidéo (sujet + texte)
          </button>
        </div>

      <div className="mt-4 rounded-xl border border-white/10 bg-surface p-4" data-testid="episode-theme-block">
        <label htmlFor="episode-theme" className="flex items-center gap-2 text-sm font-600 text-white">
          <Sparkles size={15} className="text-cyan" /> Thème de l'épisode (optionnel)
        </label>
        <p className="mt-1 text-xs text-ink-secondary">
          Fixez un thème précis sur une ligne, puis cliquez sur « Générer un épisode » : l'IA rédige l'épisode complet
          (texte + image ChatGPT) et le prépare en préparation, prêt à publier. Laissez vide pour un thème automatique.
        </p>
        <input
          id="episode-theme"
          value={epTopic}
          onChange={(e) => setEpTopic(e.target.value)}
          disabled={genLoading}
          data-testid="episode-theme-input"
          placeholder="Ex : comment transformer une idée d'application en produit réel"
          className="mt-3 w-full rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary focus:border-cyan focus:outline-none disabled:opacity-60"
        />
      </div>


      {genProgress > 0 && (
        <div className="mt-6 rounded-xl border border-cyan/20 bg-cyan/5 px-4 py-3" data-testid="gen-progress">
          <div className="mb-1.5 flex items-center justify-between text-xs text-ink-secondary">
            <span className="flex items-center gap-2"><Loader2 size={13} className="animate-spin text-cyan" /> {genLabel}</span>
            <span className="tabular-nums text-cyan">{Math.round(genProgress)}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-cyan transition-all duration-500 ease-out" style={{ width: `${genProgress}%` }} data-testid="gen-progress-bar" />
          </div>
        </div>
      )}
      </div>
      </>

      {/* Section « abonnes » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-abonnes"><Users size={13} /> Abonnés &amp; partages</div>
      <div data-testid="pub-panel-abonnes">
      <div className="mt-6 flex items-center gap-2 rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-ink-secondary" data-testid="pub-subs-count">
        <Users size={15} className="text-cyan" /> <span className="text-white font-600">{subs.length}</span> abonné(s) au blog
      </div>

      {shares.length > 0 && (
        <div className="mt-4 rounded-xl border border-white/10 bg-surface p-4" data-testid="pub-shares-panel">
          <div className="mb-3 flex items-center gap-2 text-sm text-ink-secondary">
            <Share2 size={15} className="text-cyan" /> Partages par personne
          </div>
          <div className="space-y-2">
            {shares.slice(0, 20).map((s) => (
              <div key={s.ref} className="flex items-center justify-between border-b border-white/5 pb-2 text-sm last:border-0" data-testid={`pub-share-${s.ref}`}>
                <span className="truncate text-white">{s.name || s.email || (s.ref.startsWith("anon-") ? "Visiteur anonyme" : s.ref)}</span>
                <span className="ml-3 shrink-0 text-ink-tertiary">{s.count} partage{s.count > 1 ? "s" : ""} · {s.articles} article{s.articles > 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
      </>

      {/* Section « export » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-export"><Package size={13} /> Export / Import</div>
      <div className="mt-6 rounded-xl border border-cyan/25 bg-cyan/[0.06] p-4" data-testid="pub-portability">
        <div className="flex items-center gap-2 text-sm font-600 text-white"><Package size={15} className="text-cyan" /> Copier le blog vers le site en ligne</div>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-secondary">
          La preview et le site en ligne ont des bases de données séparées. Exportez ici (articles + images + vidéos),
          déployez, puis importez le fichier depuis l’admin du site en ligne — sans re-générer ni payer de crédits.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={exportBlog} disabled={porting} data-testid="pub-export"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white transition-colors hover:border-cyan disabled:opacity-60">
            {porting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} className="text-cyan" />} Exporter le blog (ZIP)
          </button>
          <label data-testid="pub-import-label"
            className="inline-flex cursor-pointer items-center gap-2 border border-cyan/40 bg-cyan/10 px-4 py-2.5 text-sm font-600 text-cyan transition-all hover:bg-cyan/20">
            {porting ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Importer le blog (ZIP)
            <input type="file" accept=".zip" onChange={importBlog} disabled={porting} data-testid="pub-import-input" className="hidden" />
          </label>
        </div>
      </div>
      </>

      {/* Section « publications » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-publications"><Newspaper size={13} /> Publications</div>
      <div className="mt-6 space-y-3" data-testid="pub-panel-publications">
        {posts.length === 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-white/15 p-10 text-center text-sm text-ink-tertiary">
            <Info size={15} /> Aucune publication pour le moment. Générez la première avec l’IA.
          </div>
        )}
        {posts.map((p) => (
          <div key={p.id} className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-surface p-4 sm:flex-row sm:items-center" data-testid={`pub-row-${p.id}`}>
            <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-lg bg-elevated">
              {p.has_image ? (
                <>
                  <img src={imgUrl(p.id, p.image_rev)} alt="" className="h-full w-full object-cover" data-testid={`pub-cover-${p.id}`} />
                  {p.kind === "video" && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25 text-white"><Play size={20} /></span>
                  )}
                </>
              ) : p.kind === "video" ? (
                <div className="flex h-full w-full items-center justify-center text-cyan">
                  {p.generating ? <Loader2 size={20} className="animate-spin" /> : <Play size={22} />}
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-cyan/30"><Rocket size={22} /></div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {p.kind === "video" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-cyan/15 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-cyan"><Film size={11} /> Vidéo</span>
                )}
                {p.generating ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-300"><Loader2 size={11} className="animate-spin" /> En génération…</span>
                ) : (
                  <>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider ${p.status === "published" ? "bg-cyan/15 text-cyan" : p.scheduled_at ? "bg-blue-400/15 text-blue-300" : "bg-amber-400/15 text-amber-300"}`} data-testid={`pub-status-${p.id}`}>
                    {p.status === "published" ? "Publié" : p.scheduled_at ? "Programmée" : "En préparation"}
                  </span>
                  {p.status !== "published" && p.scheduled_at && (
                    <span className="rounded-full border border-blue-400/30 px-2.5 py-0.5 text-[10px] font-600 text-blue-300" data-testid={`pub-scheduled-${p.id}`}>
                      le {fmtDate(p.scheduled_at)}
                    </span>
                  )}
                  </>
                )}
                <span className="text-[11px] text-ink-tertiary">{p.status === "published" ? `Publié le ${fmtDate(p.published_at)}` : `Créé le ${fmtDate(p.created_at)}`}</span>
                <span className="inline-flex items-center gap-1 text-[11px] text-ink-tertiary" data-testid={`pub-views-${p.id}`}><Eye size={12} className="text-cyan" /> {p.views || 0} vue{(p.views || 0) > 1 ? "s" : ""}</span>
                <span className="inline-flex items-center gap-1 text-[11px] text-ink-tertiary"><Share2 size={12} className="text-cyan" /> {p.shares || 0} partage{(p.shares || 0) > 1 ? "s" : ""}</span>
                {p.kind !== "video" && p.episode_number && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-cyan/15 px-2.5 py-0.5 text-[10px] font-600 uppercase tracking-wider text-cyan" data-testid={`pub-episode-${p.id}`}>
                    Épisode {p.episode_number}
                  </span>
                )}
              </div>
              <div className="mt-1.5 truncate font-display text-base font-600 text-white">{p.title}</div>
              <div className="mt-0.5 line-clamp-1 text-sm text-ink-secondary">{p.excerpt}</div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button onClick={() => { setEditTab("apercu"); setEdit(p); }} data-testid={`pub-live-${p.id}`}
                title="Voir en live — fenêtre pleine page dans l'admin : lire l'article complet, le modifier, ajouter photo/vidéo, fermer à volonté"
                className="inline-flex items-center gap-1.5 border border-cyan/40 bg-cyan/10 px-3 py-2 text-xs font-600 text-cyan transition-colors hover:bg-cyan/20">
                <Maximize2 size={14} /> Voir en live</button>
              <button onClick={() => togglePublish(p)} disabled={p.generating} data-testid={`pub-toggle-${p.id}`} title={p.status === "published" ? "Dépublier" : "Publier"}
                className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs text-white transition-colors hover:border-cyan disabled:opacity-40">
                {p.status === "published" ? <><EyeOff size={14} /> Dépublier</> : <><Eye size={14} className="text-cyan" /> Publier</>}
              </button>
              {p.kind !== "video" && (
                <button onClick={() => regenerate(p)} disabled={regenId === p.id} data-testid={`pub-regen-${p.id}`} title="Régénérer l'article (IA)"
                  className="flex h-9 w-9 items-center justify-center border border-white/15 text-ink-secondary transition-colors hover:border-cyan hover:text-cyan disabled:opacity-60">
                  {regenId === p.id ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}</button>
              )}
              {p.kind !== "video" && (
                <button onClick={() => sendNewsletter(p)} disabled={regenId === p.id || p.status !== "published"} data-testid={`pub-newsletter-${p.id}`}
                  title={p.newsletter_sent ? "Renvoyer la newsletter aux abonnés" : "Envoyer la newsletter aux abonnés"}
                  className="flex h-9 w-9 items-center justify-center border border-white/15 text-ink-secondary transition-colors hover:border-cyan hover:text-cyan disabled:opacity-40">
                  <Mail size={15} className={p.newsletter_sent ? "text-cyan" : ""} /></button>
              )}
              {p.kind === "video" && (
                <>
                  <button onClick={() => regenerateVideo(p)} disabled={regenId === p.id || p.generating} data-testid={`pub-regen-video-${p.id}`} title="Régénérer la vidéo (IA, 12 s)"
                    className="flex h-9 w-9 items-center justify-center border border-white/15 text-ink-secondary transition-colors hover:border-cyan hover:text-cyan disabled:opacity-60">
                    {regenId === p.id ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />}</button>
                  {p.has_video && (
                    <a href={`${videoUrl(p.id)}?download=1`} data-testid={`pub-download-video-${p.id}`} title="Télécharger la vidéo"
                      className="flex h-9 w-9 items-center justify-center border border-white/15 text-ink-secondary transition-colors hover:border-cyan hover:text-cyan"><Download size={15} /></a>
                  )}
                </>
              )}
              <button onClick={() => copyShare(p)} data-testid={`pub-share-${p.id}`} title="Copier le lien de partage (aperçu image/vidéo inclus)"
                className="flex h-9 w-9 items-center justify-center border border-white/15 text-ink-secondary transition-colors hover:border-cyan hover:text-cyan">
                {copiedId === p.id ? <Check size={15} className="text-cyan" /> : <Link2 size={15} />}</button>
              <button onClick={() => { setEditTab("modifier"); setEdit(p); }} data-testid={`pub-edit-${p.id}`} title="Modifier"
                className="flex h-9 w-9 items-center justify-center border border-white/15 text-ink-secondary transition-colors hover:border-cyan hover:text-white"><Pencil size={15} /></button>
              <button onClick={() => del(p)} data-testid={`pub-delete-${p.id}`} title="Supprimer"
                className="flex h-9 w-9 items-center justify-center border border-white/15 text-ink-secondary transition-colors hover:border-red-400 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      </>

      {edit && <EditModal post={edit} initialTab={editTab} onClose={() => setEdit(null)} onSaved={(stay) => { if (!stay) setEdit(null); load(); }} />}

      {cvOpen && (
        <div className="aca-modal fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="custom-video-modal" onClick={() => !genLoading && setCvOpen(false)}>
          <div className="aca-section-scroll max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-700 text-white">Vidéo — sujet + texte</h2>
              <button onClick={() => setCvOpen(false)} data-testid="cv-close" className="text-ink-secondary hover:text-white"><X size={20} /></button>
            </div>
            <p className="mt-2 text-sm text-ink-secondary">Le texte sera dit par une voix française claire et dynamique, en 12 secondes. Un épisode de blog sera aussi rédigé sur ce sujet.</p>
            <label className="mt-4 block text-xs font-600 uppercase tracking-wider text-ink-tertiary">Sujet traité dans la vidéo</label>
            <input value={cvTopic} onChange={(e) => setCvTopic(e.target.value)} data-testid="cv-topic" placeholder="Ex : le dépôt gratuit d'une idée d'application"
              className="mt-1.5 w-full rounded-lg border border-white/10 bg-void px-3 py-2.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
            <label className="mt-4 block text-xs font-600 uppercase tracking-wider text-ink-tertiary">Texte dit dans la vidéo (≈ 12 s)</label>
            <textarea value={cvText} onChange={(e) => setCvText(e.target.value)} data-testid="cv-text" rows={3} maxLength={220}
              placeholder="Ex : App Challenge Academy réalise gratuitement votre idée d'application."
              className="mt-1.5 w-full rounded-lg border border-white/10 bg-void px-3 py-2.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
            <div className={`mt-1 text-right text-xs ${cvText.length > 180 ? "text-amber-400" : "text-ink-tertiary"}`} data-testid="cv-count">{cvText.length}/220 · une phrase courte tient en 12 s</div>
            <button onClick={submitCustomVideo} disabled={genLoading} data-testid="cv-submit"
              className="aca-btn-primary mt-4 inline-flex w-full items-center justify-center gap-2 disabled:opacity-60">
              {genLoading ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />} Lancer la vidéo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EditModal({ post, onClose, onSaved, initialTab = "modifier" }) {
  const isNew = !post.id;
  const [tab, setTab] = useState(isNew ? "modifier" : initialTab);
  const [full, setFull] = useState(null);
  const [f, setF] = useState({ title: post.title || "", excerpt: post.excerpt || "", body: post.body || "", pubDate: isoToLocal(post.published_at), schedDate: isoToLocal(post.scheduled_at) });
  const [saving, setSaving] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [hasImage, setHasImage] = useState(!!post.has_image);
  const [imgVer, setImgVer] = useState(Date.now());
  const [regen, setRegen] = useState(false);
  const [vidUp, setVidUp] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Charge la fiche complète (corps, sections, FAQ, témoignage, hashtags, mentions) pour
  // l'aperçu ET le formulaire : la liste admin ne transporte plus ces champs lourds.
  useEffect(() => {
    if (isNew) { setFull(post); return; }
    api.get(`/admin/publications/${post.id}/preview`).then((r) => {
      setFull(r.data);
      setF({ title: r.data.title || "", excerpt: r.data.excerpt || "", body: r.data.body || "",
             pubDate: isoToLocal(r.data.published_at), schedDate: isoToLocal(r.data.scheduled_at) });
      setHasImage(!!r.data.has_image);
    }).catch(() => setFull(post));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);
  const art = full || post;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Veuillez choisir une image."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (max 5 Mo)."); return; }
    const reader = new FileReader();
    reader.onload = () => setNewImage(reader.result);
    reader.readAsDataURL(file);
  };

  const onVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Veuillez choisir une vidéo."); return; }
    if (file.size > 120 * 1024 * 1024) { toast.error("Vidéo trop lourde (max 120 Mo)."); return; }
    setVidUp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/publications/${post.id}/attach-video`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewImage(null); setHasImage(true); setImgVer(Date.now());
      toast.success("Vidéo ajoutée — sa première image devient l'aperçu de l'article.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setVidUp(false); }
  };

  const regenImage = async () => {
    setRegen(true);
    try {
      await api.post(`/admin/publications/${post.id}/regenerate-image`);
      setNewImage(null);
      setHasImage(true);
      setImgVer(Date.now());
      toast.success("Nouvelle image générée par l'IA.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setRegen(false); }
  };

  const save = async () => {
    if (isNew && !f.title.trim()) { toast.error("Le titre est obligatoire."); return; }
    setSaving(true);
    try {
      const { pubDate, schedDate, ...rest } = f;
      const payload = { ...rest, ...(newImage ? { image: newImage } : {}) };
      if (pubDate) payload.published_at = new Date(pubDate).toISOString();
      // Programmation : jour + heure choisis ("" = annuler la programmation)
      payload.scheduled_at = schedDate ? new Date(schedDate).toISOString() : "";
      if (isNew) {
        await api.post(`/admin/publications/manual`, payload);
        toast.success(schedDate ? "Article créé et programmé — il sera publié automatiquement." : "Article créé (En préparation).");
        onSaved(false);
      } else {
        await api.put(`/admin/publications/${post.id}`, payload);
        toast.success(schedDate ? `Publication programmée — parution automatique le ${new Date(schedDate).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}.` : "Publication enregistrée — la fenêtre reste ouverte, fermez-la quand vous voulez.");
        onSaved(true); // recharge la liste mais garde la fenêtre ouverte
        try { const r = await api.get(`/admin/publications/${post.id}/preview`); setFull(r.data); setHasImage(!!r.data.has_image); setImgVer(Date.now()); } catch { /* l'aperçu se rafraîchira à la prochaine ouverture */ }
      }
    }
    catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/80 p-2 sm:p-4 aca-modal" data-testid="pub-edit-modal">
      <div className="h-[94vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-700 text-white">{isNew ? "Créer un article" : "Publication — aperçu & édition"}</h2>
          <div className="flex items-center gap-2">
            {!isNew && (
              <a href={liveUrl(post)} target="_blank" rel="noopener noreferrer" data-testid="pub-edit-live"
                title="Ouvrir dans un nouvel onglet, tel que sur le blog (aperçu admin si non publié)"
                className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs text-ink-secondary transition-colors hover:border-cyan hover:text-cyan">
                <ExternalLink size={14} /> Ouvrir dans un onglet</a>
            )}
            <button onClick={onClose} data-testid="pub-edit-close" title="Fermer la fenêtre" className="text-ink-secondary hover:text-white"><X size={20} /></button>
          </div>
        </div>
        {!isNew && (
          <div className="mt-4 flex gap-2" data-testid="pub-view-tabs">
            <button onClick={() => setTab("apercu")} data-testid="pub-view-tab-preview"
              className={`rounded-lg border px-4 py-2 text-xs font-600 uppercase tracking-wider transition-colors ${tab === "apercu" ? "border-cyan bg-cyan/15 text-cyan" : "border-white/15 text-ink-secondary hover:border-cyan hover:text-cyan"}`}>
              Aperçu complet</button>
            <button onClick={() => setTab("modifier")} data-testid="pub-view-tab-edit"
              className={`rounded-lg border px-4 py-2 text-xs font-600 uppercase tracking-wider transition-colors ${tab === "modifier" ? "border-cyan bg-cyan/15 text-cyan" : "border-white/15 text-ink-secondary hover:border-cyan hover:text-cyan"}`}>
              Modifier</button>
          </div>
        )}
        {tab === "apercu" && !isNew ? (
          <div className="mt-5" data-testid="pub-view-preview">
            {art.has_video ? (
              <div className={`overflow-hidden rounded-xl border border-white/10 bg-black ${art.vertical ? "mx-auto max-w-[340px]" : ""}`}>
                <video src={videoUrl(post.id)} poster={art.has_image ? imgUrl(post.id, imgVer) : undefined} controls playsInline className="w-full" data-testid="pub-view-video" />
              </div>
            ) : (art.has_image || newImage) && (
              <div className="overflow-hidden rounded-xl border border-white/10">
                <img src={newImage || imgUrl(post.id, imgVer)} alt="" className="w-full object-cover" data-testid="pub-view-cover" />
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px]">
              <span className={`rounded-full px-2.5 py-0.5 uppercase tracking-wider ${art.status === "published" ? "bg-cyan/15 text-cyan" : art.scheduled_at ? "bg-blue-400/15 text-blue-300" : "bg-amber-400/15 text-amber-300"}`} data-testid="pub-view-status">
                {art.status === "published" ? `Publié le ${fmtDate(art.published_at)}` : art.scheduled_at ? `Programmée pour le ${fmtDate(art.scheduled_at)}` : "En préparation (brouillon)"}
              </span>
              {art.episode_number && <span className="rounded-full bg-cyan/15 px-2.5 py-0.5 font-600 uppercase tracking-wider text-cyan">Épisode {art.episode_number}</span>}
            </div>
            <h3 className="mt-3 font-display text-2xl font-900 leading-tight text-white sm:text-3xl">{art.title}</h3>
            {art.excerpt && <p className="mt-2 text-base text-ink-secondary">{art.excerpt}</p>}
            <div className="mt-5 space-y-3 text-sm leading-relaxed text-ink-secondary" data-testid="pub-view-body">
              {(art.body || "").split("\n").filter((l) => l.trim()).map((para, i) => <p key={i}>{para}</p>)}
            </div>
            {(art.sections || []).length > 0 && (
              <div className="mt-6 space-y-6" data-testid="pub-view-sections">
                {art.sections.map((s, i) => (
                  <section key={i} data-testid={`pub-view-section-${i}`}>
                    <h4 className="font-display text-lg font-700 text-white">{s.heading}</h4>
                    <div className="mt-2 space-y-2 text-sm leading-relaxed text-ink-secondary">
                      {(s.content || "").split("\n").filter((l) => l.trim()).map((para, j) => <p key={j}>{para}</p>)}
                    </div>
                  </section>
                ))}
              </div>
            )}
            {(art.faq || []).length > 0 && (
              <div className="mt-6" data-testid="pub-view-faq">
                <h4 className="font-display text-lg font-700 text-white">Questions fréquentes</h4>
                <div className="mt-3 space-y-2.5">
                  {art.faq.map((qa, i) => (
                    <div key={i} className="rounded-lg border border-white/10 bg-surface p-4">
                      <div className="text-sm font-600 text-white">{qa.q}</div>
                      <p className="mt-1 text-sm text-ink-secondary">{qa.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {art.testimonial && art.testimonial.quote && (
              <figure className="mt-6 rounded-xl border border-cyan/20 bg-cyan/5 p-5" data-testid="pub-view-testimonial">
                <blockquote className="text-sm italic leading-relaxed text-white">« {art.testimonial.quote} »</blockquote>
                <figcaption className="mt-2 text-xs text-ink-secondary"><span className="font-600 text-white">{art.testimonial.author}</span>{art.testimonial.role ? ` — ${art.testimonial.role}` : ""}</figcaption>
              </figure>
            )}
            {((art.hashtags || []).length > 0 || (art.mentions || []).length > 0) && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="pub-view-tags">
                {(art.hashtags || []).map((h, i) => <span key={`h${i}`} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-cyan">{h}</span>)}
                {(art.mentions || []).map((m, i) => <span key={`m${i}`} className="rounded-full border border-cyan/25 bg-cyan/[0.06] px-3 py-1 text-xs font-600 text-cyan">{m}</span>)}
              </div>
            )}
            <div className="mt-8 flex justify-end gap-2 border-t border-white/10 pt-5">
              <button onClick={onClose} className="border border-white/15 px-5 py-2.5 text-sm text-ink-secondary hover:text-white">Fermer</button>
              <button onClick={() => setTab("modifier")} data-testid="pub-view-goto-edit"
                className="inline-flex items-center gap-2 bg-cyan px-6 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan">
                <Pencil size={15} /> Modifier cet article</button>
            </div>
          </div>
        ) : (<>
        {(hasImage || newImage) && (
          <div className="mt-5 overflow-hidden rounded-xl border border-white/10">
            <img src={newImage || imgUrl(post.id, imgVer)} alt="" className="w-full object-cover" />
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label data-testid="pub-edit-photo-upload" className="inline-flex cursor-pointer items-center gap-2 border border-white/15 px-4 py-2 text-xs text-white transition-colors hover:border-cyan">
            <Upload size={14} className="text-cyan" /> Changer la photo
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
          {!isNew && (
            <label data-testid="pub-edit-video-upload" className="inline-flex cursor-pointer items-center gap-2 border border-white/15 px-4 py-2 text-xs text-white transition-colors hover:border-cyan">
              {vidUp ? <Loader2 size={14} className="animate-spin text-cyan" /> : <Film size={14} className="text-cyan" />} Remplacer par une vidéo
              <input type="file" accept="video/*" onChange={onVideoFile} disabled={vidUp} className="hidden" />
            </label>
          )}
          {!isNew && (
            <button type="button" onClick={regenImage} disabled={regen} data-testid="pub-edit-photo-regen"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs text-white transition-colors hover:border-cyan disabled:opacity-60">
              {regen ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} className="text-cyan" />} Régénérer par IA
            </button>
          )}
          {newImage && <span className="text-xs text-cyan">Nouvelle photo prête — cliquez sur « Enregistrer ».</span>}
        </div>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Titre</span>
            <input value={f.title} onChange={set("title")} data-testid="pub-edit-title"
              className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Résumé</span>
            <input value={f.excerpt} onChange={set("excerpt")} data-testid="pub-edit-excerpt"
              className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Contenu</span>
            <textarea rows={8} value={f.body} onChange={set("body")} data-testid="pub-edit-body"
              className="w-full resize-none rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Date de parution</span>
            <input type="datetime-local" value={f.pubDate} onChange={set("pubDate")} data-testid="pub-edit-date"
              className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan [color-scheme:dark]" />
            <span className="mt-1 block text-[11px] text-ink-tertiary">Modifiez la date d’affichage de l’article sur le blog.</span>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Programmer la publication (jour et heure)</span>
            <input type="datetime-local" value={f.schedDate} onChange={set("schedDate")} data-testid="pub-edit-schedule"
              className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan [color-scheme:dark]" />
            <span className="mt-1 block text-[11px] text-ink-tertiary">
              Tant qu'elle n'est pas publiée, la publication passe au statut « Programmée » avec cette date et cette heure,
              puis elle paraît toute seule au moment choisi. Videz le champ pour annuler la programmation.
            </span>
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="border border-white/15 px-5 py-2.5 text-sm text-ink-secondary hover:text-white">Fermer</button>
          <button onClick={save} disabled={saving} data-testid="pub-edit-save"
            className="inline-flex items-center gap-2 bg-cyan px-6 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : null} Enregistrer
          </button>
        </div>
        </>)}
      </div>
    </div>
  );
}
